import { computed, makeObservable, observable } from 'mobx';
import { Ajax2 } from '@shared/Ajax';
import { Notify } from '@shared/ui-native/Notify';
import { FileTreeService } from '@shared/services/FileTreeService';
import { alphaNumSort } from '@shared/utils/utils-sort';
import { DateUtils } from '@shared/utils/DateUtils';

export class SimpleMeasurementsStore {
    @observable items: ISimpleMeasurement[] = null;
    @observable isLoading = false;

    constructor() {
        makeObservable(this);
    }

    fetchAll = async () => {
        this.isLoading = true;
        try {
            const response = await Ajax2({
                url: kuura.getContextPath() + '/api/1/map-measurements',
                method: 'GET',
                params: { projectId: kuura.getProjectId() },
            });
            this.items = (response as ISimpleMeasurement[]).map(item => ({
                ...item,
                type: 'measurement',
                measurements: typeof item.measurements === 'string' ? JSON.parse(item.measurements as string) : item.measurements,
            }));
            return this.items;
        } catch (e) {
            console.log('fetchAll error:', e);
            return null;
        } finally {
            this.isLoading = false;
        }
    };

    create = async (data: Partial<ISimpleMeasurement>): Promise<boolean> => {
        this.isLoading = true;
        try {
            await Ajax2({
                url: kuura.getContextPath() + '/api/1/map-measurement',
                method: 'POST',
                contentType: 'json',
                data: {
                    ...data,
                    measurements: JSON.stringify(data.measurements),
                },
            });
            return true;
        } catch (e) {
            Notify.error();
            console.log('create error:', e);
            return false;
        } finally {
            this.isLoading = false;
        }
    };

    update = async (uuid: string, data: Partial<ISimpleMeasurement>): Promise<boolean> => {
        this.isLoading = true;
        try {
            const payload: Record<string, unknown> = { ...data };
            if (data.measurements) {
                payload.measurements = JSON.stringify(data.measurements);
            }
            await Ajax2({
                url: kuura.getContextPath() + '/api/1/map-measurement/' + uuid,
                method: 'POST',
                contentType: 'json',
                data: payload,
            });
            return true;
        } catch (e) {
            Notify.error();
            console.log('update error:', e);
            return false;
        } finally {
            this.isLoading = false;
        }
    };

    delete = async (uuid: string): Promise<boolean> => {
        this.isLoading = true;
        try {
            await Ajax2({
                url: kuura.getContextPath() + '/api/1/map-measurement/' + uuid,
                method: 'DELETE',
            });
            return true;
        } catch (e) {
            Notify.error();
            console.log('delete error:', e);
            return false;
        } finally {
            this.isLoading = false;
        }
    };

    getByUuid = (uuid: string): ISimpleMeasurement | null => {
        return this.items?.find(item => item.uuid === uuid) ?? null;
    };
}
